import { useState, useContext, useEffect, useMemo, useRef } from 'react'
import { PassContext } from '../pass-context'
import { flatten } from "flat"
import _ from "lodash"


import { TextField, Typography, Button, FormControlLabel, FormLabel, RadioGroup, Radio, Switch, Stack, Tooltip } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'

import dataSelector from './data-selector'
import InstanceDataSelector from '/utils/instance-data-selector'
import findPathByValue from '/utils/find-path-by-value'
import PopupShell from '../popup-shell'

const PopupDialogText = ({ open, close, title, path, colors }) => {

  const { template, instanceData, setTemplate, scannerInstanceData, openInstanceDataManager } = useContext(PassContext)

  const [newTemplate, setNewTemplate] = useState({})
  const [sourcePaths, setSourcePaths] = useState([])
  const [dataSource, setDataSource] = useState("template")

  const dataChanged = useMemo(() => {
    if (open) {
      if (!_.isEqual(template, newTemplate)) return true
    }
    return false
  }, [newTemplate])

  // Runs on "open" state change or if template / instanceData change 
  // Ensures we have the current data rather than something left behind from previous form 
  useEffect(() => {
    if (open) {
      setNewTemplate(_.cloneDeep(template))

      let templateValue = _.get(template, path)
      let source = templateValue.toString().match(/^{{.*}}$/) ? "data" : "template"

      setDataSource(source)
    }
  }, [open, path, template])

  // Refresh our template / instance data source paths if instancedata changes
  useEffect(() => {
    setSourcePaths(Object.keys(flatten({ ...instanceData, __scanner_data__:{...scannerInstanceData} })).map(p => `{{${p}}}`).sort())
  }, [instanceData, scannerInstanceData])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }

  const saveFormData = () => {
    if (!_.isEqual(newTemplate, template)) setTemplate(newTemplate)
    close()
  }

  const handleChangeDataSource = value => {
    setDataSource(value)

    const newTemplateValue = (value === "template") ? dataSelector({ template: newTemplate, path, instanceData, scannerInstanceData }) : findPathByValue(instanceData, _.get(newTemplate, path))

    setNewTemplate(prev => {
      let nd = { ...prev }
      _.set(nd, path, newTemplateValue)
      return nd
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  try {
    let item = path.split(".").pop()

    return (
      <PopupShell open={open} onClose={handleClose}>
        <div className="flex flex-col h-full gap-2">

          <div className="flex flex-row justify-between mt-5">
            <Typography color="secondary" variant="h6">{title}</Typography>
            <Tooltip title="You can press ESC on your keyboard to dismiss"><Button endIcon={<EastRoundedIcon />} onClick={handleClose} >cancel</Button></Tooltip>
          </div>
          <div>
            {colors?.[item] && <span className="popup-colour-source-path"><b>colour source :</b> {colors[item].replace("model.", "")}</span>}
          </div>

          <hr />

          <div className='mt-5 overflow-y-scroll flex flex-col gap-5 mb-[100px]'>

            <FormLabel id="data_type_label">Data Source</FormLabel>
            <RadioGroup
              row
              aria-labelledby="data_type_label"
              value={dataSource}
              onChange={e => handleChangeDataSource(e.target.value)}
            >
              <FormControlLabel value="template" control={<Radio />} label="Template (fixed for all passes)" />
              <FormControlLabel value="data" control={<Radio />} label="Instance (unique for each pass)" />
            </RadioGroup>

            {dataSource === "template" && <DataSourceTemplate path={path} newTemplate={newTemplate} setNewTemplate={setNewTemplate} />}
            {dataSource === "data" && <DataSourceInstance path={path} newTemplate={newTemplate} setNewTemplate={setNewTemplate} instanceData={instanceData} sourcePaths={sourcePaths} scannerInstanceData={scannerInstanceData} />}

          </div>

          <div className='fixed bottom-5 w-[460px]'>
            <hr className='mb-5' />
            <div className="flex flex-row gap-5 justify-between">
              <Button startIcon={<DatasetOutlinedIcon />} onClick={openInstanceDataManager} >Edit sample data</Button>
              <Button variant="contained" onClick={saveFormData} disabled={!dataChanged}>Save</Button>
            </div>
          </div>

        </div>
      </PopupShell >
    )
  }
  catch (err) {
    throw err
  }
}

export default PopupDialogText


//////////////////////////////////////////////////////////////////////////////////////////////////////////
// Controlled JSX (Must have values passes as paramets and not access them from the parent 
// - hence outside the main function). Otherwise they do not update when data changes!
//////////////////////////////////////////////////////////////////////////////////////////////////////////

const DataSourceTemplate = ({ newTemplate, path, setNewTemplate }) => {

  const templateValue = _.get(newTemplate, path)

  const changeHandler = value => {
    setNewTemplate(prev => {
      let nd = { ...prev }
      _.set(nd, path, value)
      return nd
    })
  }

  if (typeof templateValue !== "undefined") return (
    <TextField label="Value"
      value={templateValue}
      slotProps={{ inputLabel: { shrink: true } }}
      type="search"
      onChange={e => changeHandler(e.target.value)}
      className="popupEditorMenuItem"
    />
  )
}

const DataSourceInstance = ({ newTemplate, path, setNewTemplate, instanceData, sourcePaths, scannerInstanceData }) => {

  try {
    let templateValue = _.get(newTemplate, path)
    let strippedValue = templateValue.replace(/{{[^}]*}}/, "")

    const [expertMode, setExpertMode] = useState(strippedValue.length > 0)

    const changeEditMode = mode => {
      if (!mode) {
        let newValue = _.get(newTemplate, path)?.replace(/.*({{[^}]*}}).*/, "$1")
        if (!/^{{.*}}$/.test(newValue)) newValue = ""

        setNewTemplate(prev => {
          let nd = { ...prev }
          _.set(nd, path, newValue)
          return nd
        })
      }

      setExpertMode(mode)
    }

    if (typeof templateValue !== "undefined") return (
      <>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography>Edit mode : Simple selection</Typography>
          <Switch checked={expertMode} onChange={e => changeEditMode(e.target.checked)} />
          <Typography>Expert mode</Typography>
        </Stack>

        <SimpleSelection active={!expertMode} newTemplate={newTemplate} setNewTemplate={setNewTemplate} instanceData={instanceData} sourcePaths={sourcePaths} path={path} scannerInstanceData={scannerInstanceData} />
        <ExpertSelection active={expertMode} newTemplate={newTemplate} path={path} setNewTemplate={setNewTemplate} instanceData={instanceData} scannerInstanceData={scannerInstanceData} sourcePaths={sourcePaths} />
      </>
    )
  }
  catch (err) {
    console.error(err)
  }
}


const SimpleSelection = ({ active, setNewTemplate, newTemplate, instanceData, path, sourcePaths, scannerInstanceData }) => {

  const templateValue = _.get(newTemplate, path)
  const instanceDataValue = dataSelector({ template: newTemplate, instanceData, scannerInstanceData, path })

  const changeHandler = value => {
    setNewTemplate(prev => {
      let nd = { ...prev }
      _.set(nd, path, value)
      return nd
    })
  }

  if (active && typeof instanceDataValue !== "undefined") return (
    <>
      <InstanceDataSelector updateValue={changeHandler} sourcePaths={sourcePaths} value={templateValue} dataSelector={dataSelector} instanceData={instanceData} scannerInstanceData={scannerInstanceData} />

      <TextField label="Sample value"
        value={instanceDataValue}
        slotProps={{ inputLabel: { shrink: true } }}
        type="search"
        disabled={true}
        fullWidth
      />
    </>
  )
}

const ExpertSelection = ({ active, newTemplate, setNewTemplate, instanceData, scannerInstanceData, path, sourcePaths }) => {
  const inputRef = useRef(null)

  let value = _.get(newTemplate, path)

  let resolvedDataValue = dataSelector({ template: newTemplate, instanceData, scannerInstanceData, path })

  const changeHandler = value => {
    setNewTemplate(prev => {
      let nd = { ...prev }
      _.set(nd, path, value)
      return nd
    })
  }

  const insertAtCursor = insertText => {
    const input = inputRef.current
    if (!input) return

    const start = input.selectionStart
    const end = input.selectionEnd

    // New value with inserted text
    const newValue = value.substring(0, start) + insertText + value.substring(end)

    changeHandler(newValue)

    // Update cursor position after insertion
    requestAnimationFrame(() => {
      input.setSelectionRange(start + insertText.length, start + insertText.length)
      input.focus()
    })
  }

  if (active) return (
    <>
      <InstanceDataSelector updateValue={insertAtCursor} sourcePaths={sourcePaths} value={"choose"} dataSelector={dataSelector} instanceData={instanceData} scannerInstanceData={scannerInstanceData} limitedEditingOnly={false} />

      <TextField
        inputRef={inputRef}
        label="Value"
        slotProps={{ inputLabel: { shrink: true } }}
        value={value}
        onChange={e => changeHandler(e.target.value)}
        fullWidth
      />

      <TextField
        label="Resolved value"
        value={resolvedDataValue}
        slotProps={{ inputLabel: { shrink: true } }}
        disabled={true}
        fullWidth
      />
    </>
  )
}
